import { isWithinInterval } from 'date-fns/isWithinInterval';
import { PostHog } from 'posthog-js';

import { config } from '~/config';
import { apiFetch } from '~/utils/api-fetch/apiFetch';
import { create } from '~/utils/store/createStore';

export enum SupportedLanguages {
  EN = 'en',
  JA = 'ja',
  PT = 'pt',
  KO = 'ko',
}

interface FlagsLanguageEnabled {
  enabled: boolean;
}

export interface FlagsMaintenance {
  from: string;
  to: string;
}

export type FlagsLanguage = {
  [key in SupportedLanguages]?: FlagsLanguageEnabled;
};

export interface SystemFlags {
  maintenance?: FlagsMaintenance;
  languages?: FlagsLanguage;
  feature_web: FeatureFlag[];
}

const flagsEndpoint = `system/flags/v1`;

export const FEATURE_FLAGS = [
  'sample',
  'hide_alfred_download_button',
  'catalysts_company_insights',
  'catalysts_theme_insights',
  'catalysts_geo_insights',
  'catalysts_macro_insights',
  'alfred_user_upload',
  'stream_feed',
  'stream_feed_v2',
  'alfred_trusted_sources',
  'live_candles_feature_flag',
  'kg_brands_products',
] as const;

export type FeatureFlag = typeof FEATURE_FLAGS[number];

const featureFlagSet = new Set<string>(FEATURE_FLAGS);

const isFeatureFlag = (value: string): value is FeatureFlag =>
  featureFlagSet.has(value);

interface SystemFlagsStore {
  systemFlags: Omit<SystemFlags, 'feature_web'>;
  featureFlags: FeatureFlag[];
  posthogClient?: PostHog;
  initialise(posthogClient?: PostHog): Promise<void>;
  checkIsInMaintenance(): boolean;
  getFeatureFlagState: (flag: FeatureFlag) => boolean;
}
const configFlags = config.app.featureFlags.filter(isFeatureFlag);
export const useFlags = create<SystemFlagsStore>((set, get) => ({
  systemFlags: {},
  getFeatureFlagState: (flag: FeatureFlag) => get().featureFlags.includes(flag),
  featureFlags: configFlags,
  initialise: async posthogClient => {
    const systemFlags = await apiFetch<SystemFlags>(flagsEndpoint);

    if (posthogClient) {
      set({
        posthogClient,
      });

      posthogClient.onFeatureFlags(() => {
        const posthogFlags = posthogClient.featureFlags.getFlagsWithDetails();

        set(state => {
          const posthogFlagKeys = new Set(Object.keys(posthogFlags));
          const enabledFlags = Object.entries(posthogFlags)
            .filter(([_, details]) => details.enabled)
            .map(([key]) => key)
            .filter(isFeatureFlag);

          const nonPosthogFlags = state.featureFlags.filter(
            flag => !posthogFlagKeys.has(flag)
          );

          return {
            featureFlags: [...nonPosthogFlags, ...enabledFlags],
          };
        });
      });

      const flagsWithDetails = posthogClient.featureFlags.getFlagsWithDetails();

      // Flags may be empty after re-authentication; reload if needed
      if (!flagsWithDetails || Object.keys(flagsWithDetails).length === 0) {
        posthogClient.reloadFeatureFlags();
      }
    }

    if (!systemFlags.error) {
      const { feature_web: featureWeb, ...rest } = systemFlags.data;
      set(state => ({
        systemFlags: rest,
        featureFlags: [...featureWeb, ...state.featureFlags],
      }));
    }
  },
  checkIsInMaintenance: () => {
    const {
      systemFlags: { maintenance },
    } = get();

    if (maintenance) {
      const start = new Date(maintenance.from);
      const end = new Date(maintenance.to);
      return isWithinInterval(new Date(), {
        start,
        end,
      });
    }

    return false;
  },
}));
